import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUp, Mic, Plus, Square, X } from "lucide-react";
import type { ChatMessage, Household, ProposedEvent, SavedLogRef } from "@lifelog/shared";
import {
  ApiError,
  confirmChat,
  isAbortError,
  postChat,
  streamChat,
  streamingEnabled,
  api,
} from "../lib/api.js";
import { useSpeechInput } from "../lib/voice.js";
import { TimeSelector } from "./TimeSelector.js";

type LocalMessage = Pick<ChatMessage, "role" | "content">;

type Pending = {
  pendingId: string;
  confirmationText: string;
  events: ProposedEvent[];
};

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-0.5" aria-label="Thinking">
      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s] dark:bg-gray-500" />
      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s] dark:bg-gray-500" />
      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce dark:bg-gray-500" />
    </div>
  );
}

function ProposalCard({
  pending,
  removedIdx,
  onToggle,
  timeValue,
  onTimeChange,
  busy,
  onSave,
  onDismiss,
}: {
  pending: Pending;
  removedIdx: Set<number>;
  onToggle: (i: number) => void;
  timeValue: string | null;
  onTimeChange: (iso: string | null) => void;
  busy: boolean;
  onSave: () => void;
  onDismiss: () => void;
}) {
  const kept = pending.events.reduce((n, ev, i) => {
    if (removedIdx.has(i) || ev.fieldErrors) return n;
    return n + 1;
  }, 0);

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/40">
      <p className="font-medium text-amber-900 dark:text-amber-200">
        Ready to save{pending.events.length > 1 ? " — drop any you don't want:" : ":"}
      </p>
      <ul className="mt-2 space-y-1.5">
        {pending.events.map((e, i) => {
          const isRemoved = removedIdx.has(i);
          const hasErrors = Boolean(e.fieldErrors);
          return (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-0.5 text-amber-500" aria-hidden>
                •
              </span>
              <div className="min-w-0 flex-1">
                <span
                  className={
                    isRemoved || hasErrors
                      ? "text-amber-700/40 line-through dark:text-amber-200/40"
                      : "text-amber-800 dark:text-amber-200"
                  }
                >
                  {e.confirmation}
                </span>
                {hasErrors && (
                  <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">
                    {Object.entries(e.fieldErrors!)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join("; ")}
                  </p>
                )}
              </div>
              {!hasErrors && (
                <button
                  type="button"
                  onClick={() => onToggle(i)}
                  disabled={busy}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-amber-600 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-400 dark:hover:bg-amber-900/40"
                  title={isRemoved ? "Add back" : "Don't save this one"}
                  aria-label={isRemoved ? `Add back: ${e.confirmation}` : `Remove: ${e.confirmation}`}
                >
                  {isRemoved ? "↶" : <X size={14} />}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-3">
        <p className="mb-1 text-xs font-medium text-amber-800 dark:text-amber-300">When</p>
        <TimeSelector value={timeValue} onChange={onTimeChange} maxDaysBack={365} />
      </div>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={busy || kept === 0}
          className="min-h-11 flex-1 rounded-lg bg-(--accent) text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
        >
          {busy ? "Saving…" : kept > 1 ? `Save ${kept}` : "Save"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={busy}
          className="min-h-11 flex-1 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

export function ChatPane({ household }: { household: Household }) {
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [removedIdx, setRemovedIdx] = useState<Set<number>>(new Set());
  const [timeValue, setTimeValue] = useState<string | null>(null);
  const [toast, setToast] = useState<{ refs: SavedLogRef[] } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const toastTimer = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerRowRef = useRef<HTMLDivElement>(null);
  const composerControlsRef = useRef<HTMLDivElement>(null);
  const composerMirrorRef = useRef<HTMLTextAreaElement>(null);
  const attachmentPlaceholderRef = useRef<HTMLDivElement>(null);
  const stt = useSpeechInput();
  const [inputMultiline, setInputMultiline] = useState(false);
  const oneLineHeightRef = useRef<number | null>(null);

  // Measure wrapping at the compact composer width, then keep the expanded
  // layout stable once it is needed. Measuring the visible textarea after it
  // has expanded would make it bounce between narrow and wide widths.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    const mirror = composerMirrorRef.current;
    const row = composerRowRef.current;
    const placeholder = attachmentPlaceholderRef.current;
    const controls = composerControlsRef.current;
    if (!el || !mirror || !row || !placeholder || !controls) return;

    if (oneLineHeightRef.current == null) {
      const previousValue = mirror.value;
      const previousWidth = mirror.style.width;
      mirror.value = "";
      mirror.style.width = "";
      mirror.style.height = "auto";
      oneLineHeightRef.current = mirror.scrollHeight;
      mirror.value = previousValue;
      mirror.style.width = previousWidth;
    }
    const oneLineHeight = oneLineHeightRef.current;

    const rowStyle = getComputedStyle(row);
    const paddingX =
      (parseFloat(rowStyle.paddingLeft) || 0) + (parseFloat(rowStyle.paddingRight) || 0);
    const gap = parseFloat(rowStyle.columnGap) || 4;
    const compactWidth =
      row.clientWidth - paddingX - placeholder.offsetWidth - controls.offsetWidth - gap * 2;
    if (compactWidth > 0) {
      mirror.style.width = `${compactWidth}px`;
      mirror.style.height = "auto";
      setInputMultiline(mirror.scrollHeight > oneLineHeight + 4);
    }

    el.style.height = "auto";
    const maxHeight = Math.max(oneLineHeight * 8, window.innerHeight * 0.4);
    el.style.height = `${Math.min(input ? el.scrollHeight : oneLineHeight, maxHeight)}px`;
  }, [input]);

  useEffect(() => {
    setRemovedIdx(new Set());
    if (pending) {
      const first = pending.events.find((e) => !e.fieldErrors && e.kind !== "define_activity_type");
      setTimeValue(first?.occurredAt ?? null);
    }
  }, [pending]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, pending, busy]);

  function append(role: LocalMessage["role"], content: string) {
    setMessages((prev) => [...prev, { role, content }]);
  }

  function patchLastAssistant(text: string) {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === "assistant") {
        next[next.length - 1] = { role: "assistant", content: last.content + text };
      } else {
        next.push({ role: "assistant", content: text });
      }
      return next;
    });
  }

  async function send(text: string) {
    const message = text.trim();
    if (!message || busy) return;
    setInput("");
    setError(null);
    setPending(null);
    append("user", message);
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      if (streamingEnabled()) {
        append("assistant", "");
        await streamChat(
          message,
          sessionId,
          {
            onSession: (id) => setSessionId(id),
            onDelta: (delta) => patchLastAssistant(delta),
            onProposal: (p) => {
              setPending({
                pendingId: p.pendingId,
                confirmationText: p.confirmationText,
                events: p.events,
              });
              setSessionId(p.sessionId);
            },
            onDone: (id) => {
              if (id) setSessionId(id);
            },
            onError: (msg) => setError(msg),
          },
          ctrl.signal,
        );
      } else {
        const res = await postChat(message, sessionId, ctrl.signal);
        setSessionId(res.sessionId);
        append("assistant", res.text);
        if (res.type === "proposal") {
          setPending({
            pendingId: res.pendingId,
            confirmationText: res.confirmationText,
            events: res.events,
          });
        }
      }
    } catch (err) {
      if (!isAbortError(err)) {
        setError(err instanceof ApiError ? err.message : "Something went wrong.");
      }
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
      setBusy(false);
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  async function save() {
    if (!pending) return;
    const keepIndexes = pending.events
      .map((_, i) => i)
      .filter((i) => !removedIdx.has(i) && !pending.events[i]?.fieldErrors);
    if (keepIndexes.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { saved } = await confirmChat(pending.pendingId, {
        keepIndexes: keepIndexes.length === pending.events.length ? undefined : keepIndexes,
        timeMode: timeValue ? "custom" : "now",
        customIso: timeValue ?? undefined,
      });
      setPending(null);
      if (saved.length) {
        if (toastTimer.current) window.clearTimeout(toastTimer.current);
        setToast({ refs: saved });
        toastTimer.current = window.setTimeout(() => setToast(null), 8000);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  async function undoToast() {
    if (!toast) return;
    try {
      await Promise.all(
        toast.refs.map((r) =>
          api(`/logs/${encodeURIComponent(r.occurredAt)}/${encodeURIComponent(r.logId)}`, {
            method: "DELETE",
          }),
        ),
      );
      setToast(null);
      append("assistant", "Undone — nothing was kept.");
    } catch {
      setError("Undo failed.");
    }
  }

  return (
    <div className="flex h-full min-w-0 flex-col">
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {messages.length === 0 && (
          <p className="mt-10 text-center text-sm text-gray-400 dark:text-gray-600">
            Tell me what you did — e.g. &ldquo;mowed the front lawn for about 45 minutes this
            morning&rdquo; or &ldquo;changed the CPAP water yesterday evening.&rdquo; Logging for{" "}
            <span className="font-medium text-gray-600 dark:text-gray-300">{household.name}</span>.
          </p>
        )}

        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={
                "max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm " +
                (m.role === "user"
                  ? "bg-(--accent) text-(--accent-contrast)"
                  : "border border-gray-200 bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100")
              }
            >
              {m.content || (m.role === "assistant" && busy ? <TypingDots /> : "")}
            </div>
          </div>
        ))}

        {busy && messages[messages.length - 1]?.role === "user" && (
          <div className="flex justify-start">
            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
              <TypingDots />
            </div>
          </div>
        )}

        {pending && (
          <ProposalCard
            pending={pending}
            removedIdx={removedIdx}
            onToggle={(i) =>
              setRemovedIdx((prev) => {
                const next = new Set(prev);
                if (next.has(i)) next.delete(i);
                else next.add(i);
                return next;
              })
            }
            timeValue={timeValue}
            onTimeChange={setTimeValue}
            busy={busy}
            onSave={() => void save()}
            onDismiss={() => setPending(null)}
          />
        )}
      </div>

      {error && (
        <div className="px-4 pb-2 text-sm text-red-600 dark:text-red-400">{error}</div>
      )}

      {toast && (
        <div className="mx-4 mb-2 flex items-center gap-3 rounded-xl bg-gray-900 px-4 py-3 text-sm text-white dark:bg-gray-100 dark:text-gray-900">
          <span className="flex-1">
            Saved {toast.refs.length} {toast.refs.length === 1 ? "item" : "items"}
          </span>
          <button type="button" onClick={() => void undoToast()} className="font-medium underline">
            Undo
          </button>
        </div>
      )}

      <form
        className="border-t border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
        onSubmit={(e) => {
          e.preventDefault();
          void send(input);
        }}
      >
        {stt.interimText && (
          <p className="mb-1 truncate px-1 text-xs italic text-gray-400 dark:text-gray-500">
            {stt.interimText}
          </p>
        )}
        <div
          ref={composerRowRef}
          className="relative flex flex-wrap items-end justify-between gap-1 rounded-3xl border border-gray-300 bg-gray-50 p-1.5 dark:border-gray-700 dark:bg-gray-800 sm:p-2"
        >
          {/* Retained for the future attachment action without affecting today's layout. */}
          <div ref={attachmentPlaceholderRef} className={`hidden ${inputMultiline ? "order-2" : "order-1"}`}>
            <button type="button" tabIndex={-1} aria-hidden="true" className="flex min-h-11 min-w-11 items-center justify-center rounded-full p-2">
              <Plus size={20} />
            </button>
          </div>

          {/* This mirror measures the compact-width wrap without ever being visible. */}
          <textarea
            ref={composerMirrorRef}
            aria-hidden="true"
            tabIndex={-1}
            readOnly
            value={input}
            rows={1}
            className="pointer-events-none invisible absolute left-0 top-0 -z-10 resize-none whitespace-pre-wrap break-words rounded-2xl px-1.5 py-3 text-base leading-5 sm:text-sm"
          />

          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              if (stt.isRecording) stt.stop();
              setInput(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send(input);
              }
            }}
            rows={1}
            placeholder="Log something…"
            className={
              "resize-none rounded-2xl bg-transparent px-1.5 text-base leading-5 text-gray-900 outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500 sm:text-sm " +
              (inputMultiline
                ? "order-1 min-h-0 w-full basis-full py-1.5"
                : "order-2 min-h-11 flex-1 py-3")
            }
          />

          <div ref={composerControlsRef} className="order-3 ml-auto flex shrink-0 items-center gap-1.5">
            {stt.supported && (
              <button
                type="button"
                onClick={() => {
                  if (stt.isRecording) {
                    stt.stop();
                    return;
                  }
                  stt.start((text) => {
                    setInput((prev) => (prev ? `${prev.trimEnd()} ${text}` : text));
                  });
                }}
                disabled={busy}
                className={
                  "flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full p-2 " +
                  (stt.isRecording
                    ? "animate-pulse bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400"
                    : "text-gray-600 hover:bg-gray-200 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700")
                }
                aria-label={stt.isRecording ? "Stop dictation" : "Dictate"}
                title={stt.isRecording ? "Stop dictation" : "Dictate"}
              >
                <Mic size={18} />
              </button>
            )}
            {busy ? (
              <button
                type="button"
                onClick={stop}
                className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full bg-gray-800 p-2 text-white hover:bg-gray-700 dark:bg-gray-200 dark:text-gray-900 dark:hover:bg-white"
                aria-label="Stop"
                title="Stop"
              >
                <Square size={16} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!input.trim()}
                className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full bg-(--accent) p-2 text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-40"
                aria-label="Send"
                title="Send"
              >
                <ArrowUp size={18} />
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
